/**
 * Issue API Service
 * Handles API calls for issue management operations
 */

import { apiService } from './api';
import { 
  IssueInteraction, 
  IssueWithModifications, 
  IssueModificationSummary,
  APIResponse 
} from '../types';

export interface ModifyIssueRequest {
  modification_type: 'accepted' | 'rejected' | 'edited' | 'pending';
  user_notes?: string;
  modified_title?: string;
  modified_description?: string;
  modified_recommendation?: string;
  modified_risk_level?: string;
  modified_risk_analysis?: string;
  // Multi-stage workflow fields
  stage?: string;
  modification_source?: string;
}

export interface BulkModifyIssueRequest {
  issue_ids: string[];
  modification_type: 'accepted' | 'rejected' | 'edited';
  user_notes?: string;
}

export interface IssueModificationResponse {
  id: string;
  issue_id: string;
  modification_type: 'accepted' | 'rejected' | 'edited' | 'pending';
  stage?: 'original' | 'edited' | 'refined';
  modification_source?: 'user' | 'ai_refined'; 
  original_value?: string;
  modified_value?: string;
  user_notes?: string;
  created_at: string;
}

export interface ProjectIssuesWithModifications {
  project_id: string;
  issues_by_document: Record<string, IssueWithModifications[]>;
  total_issues: number;
  document_count: number;
  /** Plan 180: dismissed rows on this run, returned whether or not they are listed. */
  dismissed_count?: number;
  /**
   * Issue #1073: non-null means this run's review is known to be incomplete
   * and needs a user-triggered re-run; the value says why. Optional because a
   * cached payload from before this shipped will not carry it.
   */
  needs_rerun_reason?: string | null;
}

export interface BulkModificationResult {
  successful_count: number;
  failed_count: number;
  successful_modifications: Array<{
    issue_id: string;
    modification: IssueModificationResponse;
  }>;
  failed_modifications: Array<{
    issue_id: string;
    error: string;
  }>;
  modification_type: string;
}

export interface ExportData {
  project_id: string;
  summary: IssueModificationSummary;
  issues_by_document: Record<string, IssueWithModifications[]>;
  accepted_issues: IssueWithModifications[];
  rejected_issues: IssueWithModifications[];
  edited_issues: IssueWithModifications[];
  pending_issues: IssueWithModifications[];
}

// Issue Refinement Types
export interface IssueRefinementRequest {
  user_request: string;
  template?: string;
}

export interface IssueRefinementResponse {
  title: string;
  description: string;
  risk_level: 'high' | 'medium' | 'low';
  issue_type: string;
  document_section?: string;
  recommendation?: string;
  risk_analysis?: string;
  confidence?: number;
  refinement_notes?: string;
}

export interface RefineIssueAPIResponse {
  message: string;
  refinement_id: string;
  status: 'pending' | 'completed' | 'failed';
  refined_issue?: IssueRefinementResponse;
  error_message?: string;
}

export interface IssueRefinement {
  id: string;
  issue_id: string;
  user_request: string;
  original_issue_data: Record<string, any>;
  refined_issue_data?: Record<string, any>;
  refinement_status: 'pending' | 'completed' | 'failed';
  error_message?: string;
  created_at: string;
  completed_at?: string;
}

class IssueApiService {
  /**
   * Modify an individual issue
   */
  async modifyIssue(issueId: string, request: ModifyIssueRequest): Promise<APIResponse<{
    issue_id: string;
    modification_type: string;
    modification: IssueModificationResponse;
    user_notes?: string;
  }>> {
    return apiService.patch(`/api/v1/issues/${issueId}`, request);
  }

  /**
   * Plan 180: soft-delete an individual issue (hide from default view).
   * Persistent server-side; restore via {@link restoreIssue}.
   */
  async dismissIssue(issueId: string, note?: string): Promise<void> {
    await apiService.patch(`/api/v1/issues/${issueId}/dismiss`, note ? { note } : {});
  }

  /** Plan 180: undo a previous dismiss. */
  async restoreIssue(issueId: string): Promise<void> {
    await apiService.patch(`/api/v1/issues/${issueId}/restore`, {});
  }

  /**
   * Get user modification for a specific issue
   */
  async getIssueModification(issueId: string): Promise<APIResponse<{
    issue_id: string;
    modification: IssueModificationResponse | null;
    has_modification: boolean;
  }>> {
    return apiService.get(`/api/v1/issues/${issueId}/modification`);
  }

  /**
   * Reset issue to original state (remove user modification)
   */
  async resetIssueModification(issueId: string): Promise<APIResponse<{
    issue_id: string;
    reset: boolean;
  }>> {
    return apiService.delete(`/api/v1/issues/${issueId}/modification`);
  }

  /**
   * Reset issue to original AI-generated state (multi-stage workflow compatible)
   * Removes all modifications and refinements, restoring original content
   */
  async resetIssueToOriginal(issueId: string): Promise<APIResponse<{
    issue_id: string;
    reset: boolean;
    status: string;
  }>> {
    return apiService.post(`/api/v1/issues/${issueId}/reset`, {});
  }

  /**
   * Apply bulk modifications to multiple issues
   */
  async bulkModifyIssues(projectId: string, request: BulkModifyIssueRequest): Promise<APIResponse<BulkModificationResult>> {
    return apiService.post(`/api/v1/projects/${projectId}/issues/bulk-modify`, request);
  }

  /**
   * Get all issues for a project with user modifications.
   * Plan 180: optional includeDismissed controls whether dismissed issues
   * are returned. Backend returns `dismissed_count` regardless.
   */
  async getProjectIssuesWithModifications(
    projectId: string,
    includeDismissed: boolean = false,
  ): Promise<APIResponse<ProjectIssuesWithModifications>> {
    console.log(`[IssueAPI] Fetching issues with modifications for project: ${projectId}`);
    try {
      const url = includeDismissed
        ? `/api/v1/projects/${projectId}/issues/with-modifications?include_dismissed=true`
        : `/api/v1/projects/${projectId}/issues/with-modifications`;
      const result = await apiService.get(url);
      console.log(`[IssueAPI] Successfully fetched issues:`, result);
      return result;
    } catch (error: any) {
      console.error(`[IssueAPI] Failed to fetch issues for project ${projectId}:`, error);

      if (error.response?.status === 403) {
        console.error(`[IssueAPI] 403 Forbidden - Check authentication and project access`);
        console.error(`[IssueAPI] Request details:`, {
          url: error.config?.url,
          headers: error.config?.headers,
          projectId: projectId
        });
      }

      throw error;
    }
  }

  /**
   * Get summary of issue modifications for a project
   */
  async getModificationSummary(projectId: string): Promise<IssueModificationSummary> {
    const response = await apiService.get(`/api/v1/projects/${projectId}/issues/modification-summary`);
    return response; // This endpoint returns the summary directly, not wrapped in APIResponse
  }

  /**
   * Get issue data formatted for export/reporting
   */
  async getExportData(projectId: string): Promise<APIResponse<ExportData>> {
    return apiService.get(`/api/v1/projects/${projectId}/issues/export-data`);
  }

  /**
   * Helper method to convert IssueInteraction to API request format
   */
  issueInteractionToRequest(interaction: IssueInteraction): ModifyIssueRequest | null {
    // Save pending actions if they're from AI refinement OR user edits (to persist content)
    // Skip other pending actions to avoid spamming database
    if (interaction.action === 'pending' && 
        interaction.modification_source !== 'ai_refined' && 
        interaction.modification_source !== 'user') {
      return null;
    }
    
    const request: ModifyIssueRequest = {
      modification_type: interaction.action as 'accepted' | 'rejected' | 'edited' | 'pending',
      user_notes: interaction.user_notes,
      // Include workflow fields
      stage: interaction.stage,
      modification_source: interaction.modification_source
    };

    // Include modified content for 'edited', 'accepted', and modified 'pending' actions
    // 'edited' = user is editing, 'accepted' = user is accepting a modified version
    // 'pending' with 'ai_refined' source = AI refinement awaiting user decision
    // 'pending' with 'user' source = User edit awaiting user decision
    if (interaction.action === 'edited' || 
        (interaction.action === 'accepted' && (interaction.modified_title || interaction.modified_description || interaction.modified_recommendation || interaction.modified_risk_level || interaction.modified_risk_analysis)) ||
        (interaction.action === 'pending' && interaction.modification_source === 'ai_refined') ||
        (interaction.action === 'pending' && interaction.modification_source === 'user')) {
      if (interaction.modified_title) {
        request.modified_title = interaction.modified_title;
      }
      if (interaction.modified_description) {
        request.modified_description = interaction.modified_description;
      }
      if (interaction.modified_recommendation) {
        request.modified_recommendation = interaction.modified_recommendation;
      }
      if (interaction.modified_risk_level) {
        request.modified_risk_level = interaction.modified_risk_level;
      }
      if (interaction.modified_risk_analysis) {
        request.modified_risk_analysis = interaction.modified_risk_analysis;
      }
    }

    return request;
  }

  /**
   * Helper method to convert API response to IssueInteraction format
   */
  responseToIssueInteraction(modification: IssueModificationResponse): IssueInteraction {
    const interaction: IssueInteraction = {
      issue_id: modification.issue_id,
      stage: modification.stage || 'original',
      action: modification.modification_type,
      final_action: modification.modification_type === 'accepted' || modification.modification_type === 'rejected' ? modification.modification_type : undefined,
      modification_source: modification.modification_source,
      user_notes: modification.user_notes
    };

    // Add modified content if available
    if (modification.modified_value) {
      // For now, assume it's a description modification
      // In a more complete implementation, we'd need to distinguish title vs description
      interaction.modified_description = modification.modified_value;
    }

    return interaction;
  }

  /**
   * Batch operations helper - modify multiple issues with error handling
   */
  async batchModifyIssues(
    projectId: string, 
    issueIds: string[], 
    modificationType: 'accepted' | 'rejected' | 'edited',
    userNotes?: string
  ): Promise<{
    successful: string[];
    failed: Array<{ issueId: string; error: string }>;
  }> {
    try {
      const response = await this.bulkModifyIssues(projectId, {
        issue_ids: issueIds,
        modification_type: modificationType,
        user_notes: userNotes
      });

      return {
        successful: response.data?.successful_modifications?.map(m => m.issue_id) || [],
        failed: response.data?.failed_modifications?.map(f => ({
          issueId: f.issue_id,
          error: f.error
        })) || []
      };
    } catch (error) {
      // If bulk operation fails entirely, treat all as failed
      return {
        successful: [],
        failed: issueIds.map(id => ({
          issueId: id,
          error: error instanceof Error ? error.message : 'Unknown error'
        }))
      };
    }
  }

  /**
   * Get modification statistics for a project
   */
  async getModificationStatistics(projectId: string): Promise<{
    total: number;
    modified: number;
    accepted: number;
    rejected: number;
    edited: number;
    pending: number;
    modificationRate: number;
  }> {
    try {
      const summary = await this.getModificationSummary(projectId);
      
      return {
        total: summary.total_issues,
        modified: summary.accepted_issues + summary.rejected_issues + summary.edited_issues,
        accepted: summary.accepted_issues,
        rejected: summary.rejected_issues,
        edited: summary.edited_issues,
        pending: summary.pending_issues,
        modificationRate: summary.modification_rate
      };
    } catch (error) {
      console.error('Error getting modification statistics:', error);
      return {
        total: 0,
        modified: 0,
        accepted: 0,
        rejected: 0,
        edited: 0,
        pending: 0,
        modificationRate: 0
      };
    }
  }

  // Issue Refinement Methods

  /**
   * Refine an issue using AI with custom user instructions
   */
  async refineIssueWithAI(issueId: string, request: IssueRefinementRequest): Promise<RefineIssueAPIResponse> {
    const response = await apiService.post(`/api/v1/issues/${issueId}/reprompt`, request);
    return response; // API returns RefineIssueAPIResponse directly
  }

  /**
   * Get all refinements for a specific issue
   */
  async getIssueRefinements(issueId: string): Promise<APIResponse<{
    issue_id: string;
    refinements: IssueRefinement[];
    total_refinements: number;
  }>> {
    return apiService.get(`/api/v1/issues/${issueId}/refinements`);
  }

  /**
   * Quick refinement templates for common user requests
   */
  getQuickRefinementTemplates(): Array<{ id: string; label: string; request: string }> {
    return [
      {
        id: 'more_detailed',
        label: 'Make More Detailed',
        request: 'Please provide more detailed analysis of this issue, including specific legal implications and additional context.'
      },
      {
        id: 'focus_risks',
        label: 'Focus on Risks',
        request: 'Focus primarily on the legal risks and potential liabilities associated with this issue.'
      },
      {
        id: 'focus_compliance',
        label: 'Focus on Compliance',
        request: 'Emphasize compliance considerations and regulatory requirements related to this issue.'
      },
      {
        id: 'simplify',
        label: 'Simplify Language',
        request: 'Simplify the language and explanation while maintaining legal accuracy and completeness.'
      },
      {
        id: 'business_impact',
        label: 'Business Impact',
        request: 'Focus on the business impact and commercial implications of this legal issue.'
      },
      {
        id: 'remediation_focus',
        label: 'Remediation Steps',
        request: 'Provide more detailed recommendations and specific steps for addressing this issue.'
      }
    ];
  }

  /**
   * Check if an issue has any refinements
   */
  async hasRefinements(issueId: string): Promise<boolean> {
    try {
      const response = await this.getIssueRefinements(issueId);
      return (response.data?.total_refinements || 0) > 0;
    } catch (error) {
      console.error('Error checking refinements:', error);
      return false;
    }
  }

  /**
   * Get the latest successful refinement for an issue
   */
  async getLatestSuccessfulRefinement(issueId: string): Promise<IssueRefinement | null> {
    try {
      const response = await this.getIssueRefinements(issueId);
      const completedRefinements = response.data?.refinements.filter(
        r => r.refinement_status === 'completed'
      ) || [];
      
      return completedRefinements.length > 0 ? completedRefinements[0] : null;
    } catch (error) {
      console.error('Error getting latest refinement:', error);
      return null;
    }
  }
}

// Export singleton instance
export const issueApiService = new IssueApiService();
export default issueApiService;